import { Component, inject, OnInit } from '@angular/core';
import { ButtonDirective } from 'primeng/button';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputOtpModule } from 'primeng/inputotp';
import { HttpErrorResponse } from '@angular/common/http';
import { BookmarkService } from '../../../services/bookmark/bookmark.service';
import { Bookmark } from '../../../models/bookmark';
import { HeaderComponent } from '../../atoms/header/header.component';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'app-password-protection-dialog-bookmark',
  standalone: true,
  imports: [
    ButtonDirective,
    ReactiveFormsModule,
    InputOtpModule,
    HeaderComponent,
  ],
  templateUrl: './password-protection-dialog-bookmark.component.html',
  styleUrl: './password-protection-dialog-bookmark.component.scss',
})
export class PasswordProtectionDialogBookmarkComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);

  bookmark!: Bookmark;
  passwordForm: FormGroup = this.fb.group({});
  error = '';
  loading = false;

  private bookmarkService = inject(BookmarkService);

  /**
   * Angular lifecycle hook that is called after the component's view has been initialized.
   * Initializes the password form control with a guest password field and subscribes to its value changes
   * to perform validation using a debounce time of 300ms.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit() {
    this.passwordForm = this.fb.group({
      guestPassword: ['', [Validators.required]],
    });

    // Subscribe to valueChanges after form creation
    this.passwordForm
      .get('guestPassword')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => this.check());
  }

  /**
   * Validates the guest password input and triggers the submission if the conditions are met.
   *
   * This method checks whether the password entered in the `guestPassword` field
   * is defined and has exactly 4 characters. If the validation passes, it calls the `onSubmit` method.
   *
   * @return {void} No return value.
   */
  check() {
    const password = this.passwordForm.get('guestPassword')?.value;
    if (password && password.length === 4) {
      this.onSubmit();
    }
  }

  /**
   * Handles the submission of the password form. Verifies the provided password using the
   * bookmark data and performs appropriate actions based on the result.
   *
   * @return {void} Does not return a value. Executes different actions depending on the validity of the password.
   */
  async onSubmit() {
    const token = (this.config.data.bookmark as Bookmark).guestLink?.token;
    if (!token || this.loading) return;

    this.loading = true;
    try {
      // verified on the server - the password is never sent to the client
      const bookmark = await this.bookmarkService.verifyGuestPassword(
        token,
        this.passwordForm.value.guestPassword
      );
      this.dialogRef.close({ action: 'submit', bookmark });
    } catch (error) {
      this.setError(
        error instanceof HttpErrorResponse && error.status === 429
          ? 'Zu viele Versuche. Bitte versuchen Sie es später erneut.'
          : 'Passwort inkorrekt. Bitte versuchen Sie es erneut.'
      );
    }
  }

  /**
   * Handles the cancellation of the dialog and closes it.
   * Closes the dialog with a cancellation action.
   *
   * @return {void} This method does not return a value.
   */
  onCancel() {
    this.dialogRef.close({ action: 'cancel' });
  }

  /**
   * Updates the error message and sets the loading state to false.
   *
   * @param {string} error - The error message to set.
   * @return {void} This method does not return a value.
   */
  setError(error: string) {
    this.error = error;
    this.loading = false;
  }
}
